var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { cx, css as emotionCss } from 'emotion';
import { toolTipHover, toolTipNoHover, noTransition } from '../styles/legacy';
import { Arrow, Tooltip as Component } from './Tooltip.styled';
import { locations } from '../types';
import { getAutomation, getDebug, getStyleNames, useCitrusDebug } from '@citrus/runtime';
import { isNotNullOrEmpty, escapeKeyPressed, isNotNilOrEmpty, iif, isString, isNotNil } from '@pcty/utils';
import { computePosition, autoPlacement, flip, shift as shiftFn, offset, arrow as arrowFn, } from '@floating-ui/dom';
import { useTheme } from '@citrus/user';
var VERSION = '2.2.0';
var Tooltip = React.forwardRef(function (_a, ref) {
    var _b;
    var _c = _a.arrow, arrow = _c === void 0 ? true : _c, arrowOffset = _a.arrowOffset, automationId = _a.automationId, children = _a.children, className = _a.className, css = _a.css, edgeSpacing = _a.edgeSpacing, _d = _a.flipLocation, flipLocation = _d === void 0 ? true : _d, id = _a.id, _e = _a.location, location = _e === void 0 ? 'top' : _e, _f = _a.portal, portal = _f === void 0 ? false : _f, _g = _a.shift, shift = _g === void 0 ? false : _g, _h = _a.transitionSpeed, transitionSpeed = _h === void 0 ? 'normal' : _h, useAsLabel = _a.useAsLabel, _j = _a.__TYPE, TYPE = _j === void 0 ? 'Tooltip' : _j, other = __rest(_a, ["arrow", "arrowOffset", "automationId", "children", "className", "css", "edgeSpacing", "flipLocation", "id", "location", "portal", "shift", "transitionSpeed", "useAsLabel", "__TYPE"]);
    var debug = useCitrusDebug();
    var citrus2022 = useTheme().citrus2022;
    var isCitron = citrus2022 !== null && citrus2022 !== void 0 ? citrus2022 : false;
    var _k = useState(false), tooltipOpen = _k[0], setTooltipOpen = _k[1];
    var _l = useState(false), focused = _l[0], setFocused = _l[1];
    var _m = useState(location), tooltipPlacement = _m[0], setTooltipPlacement = _m[1];
    var spaceFromEdge = edgeSpacing || iif(isCitron, 8, 4);
    var arrOffset = arrowOffset || iif(arrow, 16, 8);
    var portalRef = useRef(null);
    var addHover = function (event) {
        setFocused(event.type === 'focus');
        setTooltipOpen(true);
    };
    var removeHover = function (event) {
        setFocused(event.type === 'blur' || event.key === 'Escape');
        setTooltipOpen(false);
    };
    var handleKeyDown = useCallback(function (event) {
        if (escapeKeyPressed(event)) {
            removeHover(event);
        }
    }, []);
    useLayoutEffect(function () {
        var tooltip = document.getElementById(id);
        if (!tooltip)
            return;
        var arrowElement = tooltip.querySelector('[data-arrow="true"]');
        var referenceElement = document.querySelector("[data-citrus-tooltip=\"".concat(id, "\"]"));
        if (!referenceElement)
            return;
        if (useAsLabel && !referenceElement.ariaLabel) {
            referenceElement.setAttribute('aria-labelledby', id);
        }
        if (!useAsLabel) {
            referenceElement.setAttribute('aria-describedby', id);
        }
        var portalElement = portal
            ? document.querySelector(isString(portal) ? portal : 'body')
            : null;
        if (portal && !portalElement) {
            throw new Error("Portal element not found: ".concat(portal, ". Must be a valid css selector"));
        }
        portalRef.current = portalElement;
        var assignTooltipStyles = function (data) {
            var x = data.x, y = data.y, middlewareData = data.middlewareData;
            Object.assign(tooltip.style, {
                left: isNotNil(x) ? "".concat(x, "px") : '',
                top: isNotNil(y) ? "".concat(y, "px") : '',
            });
            if (middlewareData.arrow && arrowElement && arrowElement.style) {
                var _a = middlewareData.arrow, xArrow = _a.x, yArrow = _a.y;
                Object.assign(arrowElement.style, {
                    left: xArrow ? "".concat(xArrow - spaceFromEdge, "px") : '',
                    top: yArrow ? "".concat(yArrow - spaceFromEdge, "px") : '',
                });
            }
        };
        // Ideally, we would create a useEffect that tracked the open state of the popper
        // by itself and ran an update() to update its location, but we are unable to do this
        // due to how the component is implemented using the eventListeners and querySelectors of specific items.
        if (tooltipOpen) {
            var side = location.split('-');
            var autoPlace = iif(side[0] === 'auto', autoPlacement({ crossAxis: true, allowedPlacements: locations }), iif(flipLocation, flip(), undefined));
            var arrowMiddleware = iif(isNotNilOrEmpty(arrowElement), arrowFn({
                element: arrowElement,
                padding: spaceFromEdge,
            }), undefined);
            var shiftMw = iif(shift, shiftFn({ padding: spaceFromEdge }), undefined);
            computePosition(referenceElement, tooltip, {
                placement: location,
                middleware: [offset(arrOffset), autoPlace, shiftMw, arrowMiddleware],
            }).then(function (data) {
                setTooltipPlacement(data.placement);
                assignTooltipStyles(data);
            });
        }
        referenceElement.addEventListener('mouseover', addHover);
        referenceElement.addEventListener('focus', addHover);
        referenceElement.addEventListener('blur', removeHover);
        referenceElement.addEventListener('mouseleave', removeHover);
        tooltip.addEventListener('mouseover', addHover);
        tooltip.addEventListener('mouseleave', removeHover);
        document.addEventListener('keydown', handleKeyDown);
        return function () {
            referenceElement.removeEventListener('mouseover', addHover);
            referenceElement.removeEventListener('focus', addHover);
            referenceElement.removeEventListener('mouseleave', removeHover);
            referenceElement.removeEventListener('blur', removeHover);
            tooltip.removeEventListener('mouseover', addHover);
            tooltip.removeEventListener('mouseleave', removeHover);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [arrOffset, tooltipOpen, handleKeyDown, id, location, shift, spaceFromEdge, useAsLabel, flipLocation, portal]);
    var classes = cx((_b = {},
        _b[emotionCss(css)] = isNotNullOrEmpty(css),
        _b), className || '');
    var delayMultiplier = {
        slow: 1.5,
        normal: 1,
        fast: 0.5,
        none: 0,
    }[transitionSpeed];
    var adjustedToolTipHover = __assign(__assign({}, toolTipHover), { transitionDelay: "".concat(parseFloat(toolTipHover.transitionDelay) * delayMultiplier, "ms") });
    var adjustedToolTipNoHover = __assign(__assign({}, toolTipNoHover), { transitionDelay: "".concat(parseFloat(toolTipNoHover.transitionDelay) * delayMultiplier, "ms") });
    var tooltipStyles = __assign(__assign(__assign({}, (tooltipOpen && adjustedToolTipHover)), (!tooltipOpen && adjustedToolTipNoHover)), (focused && noTransition));
    var renderTooltip = function () { return (React.createElement(Component, __assign({ "aria-hidden": !tooltipOpen, className: classes, "data-tooltip-placement": tooltipPlacement, id: id, ref: ref, role: "tooltip", style: tooltipStyles, styleNames: getStyleNames({ toolTipHover: tooltipOpen, toolTipNoHover: !tooltipOpen, noTransition: focused }) }, other, getDebug(debug, TYPE, VERSION), getAutomation(automationId)),
        arrow && React.createElement(Arrow, { "data-arrow": "true" }),
        children)); };
    return React.createElement(React.Fragment, null, !portalRef.current ? renderTooltip() : ReactDOM.createPortal(renderTooltip(), portalRef.current));
});
Tooltip.defaultProps = {
    __TYPE: 'Tooltip',
};
export default Tooltip;
